export enum SncrQuotaType {
  NRA = 'NRA',
  NRB = 'NRB',
  NRB2 = 'NRB2',
  NRR = 'NRR',
  NRT = 'NRT',
  RET = 'RET',
  RCE = 'RCE',
}

export type SncrPrescriptionTypeId = `${SncrQuotaType}`;

export const SNCR_REGULAR_QUOTA_TYPES = [
  SncrQuotaType.NRA,
  SncrQuotaType.NRB,
  SncrQuotaType.NRB2,
  SncrQuotaType.NRR,
  SncrQuotaType.NRT,
] as const;

export const SNCR_SPECIAL_QUOTA_TYPES = [SncrQuotaType.RET, SncrQuotaType.RCE] as const;

export const SNCR_QUOTA_TYPES = [...SNCR_REGULAR_QUOTA_TYPES, ...SNCR_SPECIAL_QUOTA_TYPES] as const;

export type SncrRegularQuotaType = (typeof SNCR_REGULAR_QUOTA_TYPES)[number];
export type SncrSpecialQuotaType = (typeof SNCR_SPECIAL_QUOTA_TYPES)[number];

export interface SncrDoctorQuotaGroupedByType {
  type: SncrPrescriptionTypeId;
  quantity: number;
}

export interface SncrRegularQuotaRequest {
  type: SncrRegularQuotaType;
  quantity: number;
}

export interface SncrSpecialQuotaRequest {
  type: SncrSpecialQuotaType;
}

export interface SncrAddQuotaPayload {
  regularQuotas?: SncrRegularQuotaRequest[];
  specialQuotas?: SncrSpecialQuotaRequest[];
}

export type AddQuotaAlertType = 'WARNING' | 'ERROR';

export const DAILY_QUOTA_REACHED_CODE = 'DAILY_QUOTA_REACHED';
export const MONTHLY_QUOTA_REACHED_CODE = 'MONTHLY_QUOTA_REACHED';
export const NO_QUOTAS_AVAILABLE_CODE = 'NO_QUOTAS_AVAILABLE';
export const WRONG_PRESCRIBER_CODE = 'WRONG_PRESCRIBER';
export const WRONG_PROFESSIONAL_NUMBER_CODE = 'WRONG_PROFESSIONAL_NUMBER';
export const UNKNOWN_ERROR_CODE = 'UNKNOWN_ERROR';
export const UNKNOWN_INTERNAL_SERVER_ERROR_CODE = 'UNKNOWN_INTERNAL_SERVER_ERROR';
export const ANVISA_CFO_ERROR_CODE = 'ANVISA_CFO_ERROR';
export const ANVISA_CPF_ERROR_CODE = 'ANVISA_CPF_ERROR';

export interface AddQuotaAlert {
  code: string;
  type: AddQuotaAlertType;
  data?: Record<string, unknown> & {
    anvisaResponseStatus?: number
    professionalInfo?: { 
      professionalRecordIssuer: string;
      professionalRecord: string;
      state: string;
    }
  };
}

export interface SncrAddDoctorQuotaResponse {
  type: SncrPrescriptionTypeId;
  success: boolean;
  errorMessage?: string;
  alerts?: AddQuotaAlert[];
}

export interface SncrStock {
  type: SncrPrescriptionTypeId;
  balance: number;
  /**
   * Indica se o tipo está habilitado (feature flag ativa). Tipos ausentes na
   * resposta do backend continuam sendo listados, porém desabilitados: sem
   * checkbox funcional e sem pré-seleção por estoque crítico.
   */
  enabled: boolean;
}

export interface SncrNumberingRequestItem {
  type: SncrPrescriptionTypeId;
  quantity: number;
}

export type SncrRequestItemStatus = 'fulfilled' | 'failed';

export interface SncrNumberingRequestItemResult extends SncrNumberingRequestItem {
  status: SncrRequestItemStatus;
  errorMessage?: string;
  alerts?: AddQuotaAlert[];
}

export interface SncrNumberingRequestResult {
  items: SncrNumberingRequestItemResult[];
}

export enum AlertType {
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
}

export enum AlertCode {
  SNCR_INSUFFICIENT_QUOTA = 'SNCR_INSUFFICIENT_QUOTA',
  SNCR_RESERVE_QUOTA_ERROR = 'SNCR_RESERVE_QUOTA_ERROR',
  SNCR_QUOTA_RESERVED = 'SNCR_QUOTA_RESERVED',
}

export interface ResponseAlert {
  type: AlertType;
  code: AlertCode;
  data?: { number?: string };
}

/**
 * Retorno de `/prescricao/adicionarmedicamento`. Além do id do item criado, o
 * backend anexa `metadata.alerts` sinalizando o resultado da reserva de
 * numeração SNCR (reservada, sem saldo ou erro).
 */
export interface AddMedicineResponse {
  data?: { id: number };
  metadata?: { alerts?: ResponseAlert[] };
}
